import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useFamiliesData } from '@/hooks/useFamiliesData';
import { useFamilyActions } from '@/hooks/useFamilyActions';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';
import { Parent, Student, Family } from '@/types/models';
import AddFamilyDialog from '@/components/families/AddFamilyDialog';
import AddFamilyWithParentDialog from '@/components/families/AddFamilyWithParentDialog';
import { useFamilyDialogs } from '@/hooks/family/useFamilyDialogs';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/SearchInput';
import { Plus, Users, UserPlus, Heart } from 'lucide-react';
import { useSearch } from '@/hooks/useSearch';
import { AdminPageScaffold, DataViewScaffold } from '@/components/core';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import FamilyCard from '@/components/families/FamilyCard';
import FamilyTableRow from '@/components/families/FamilyTableRow';
import { Badge } from '@/components/ui/badge';
import { UserCheck, AlertCircle, Users as UsersIcon } from 'lucide-react';

type FilterType = "all" | "needs-parent" | "needs-student" | "active" | "inactive";

const Families = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();
  
  // Search functionality
  const { searchQuery, debouncedSearchQuery, setSearchQuery, clearSearch, hasSearch } = useSearch();
  
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");

  // Get dialog state management
  const {
    isAddFamilyDialogOpen,
    setIsAddFamilyDialogOpen,
    isAddFamilyWithParentDialogOpen,
    setIsAddFamilyWithParentDialogOpen
  } = useFamilyDialogs();
  
  const {
    families,
    loading,
    connectionError,
    parentsByFamily,
    studentsByFamily,
    setFamilies,
    setAllParents,
    setAllStudents
  } = useFamiliesData();
  
  const {
    handleSendInvite: sendInvite,
    handleEditParent,
    handleParentUpdated,
    handleFamilyAdded,
    handleFamilyWithParentAdded,
    handleStudentAdded
  } = useFamilyActions();

  // Add subscription access check - AFTER all existing hooks
  const { isHiveShareOnly, loading: subscriptionLoading } = useSubscriptionAccess();

  // Deduplicate families
  useEffect(() => {
    if (families.length > 0) {
      const uniqueFamilies = Array.from(
        new Map(families.map(family => [family.id, family])).values()
      );
      
      if (uniqueFamilies.length !== families.length) {
        setFamilies(uniqueFamilies);
      }
    }
  }, [families, setFamilies]);

  // Filter families based on search and filter criteria
  const filteredFamilies = useMemo(() => {
    let filtered = families;

    // Apply search filter using debounced query
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase().trim();
      filtered = filtered.filter(family => {
        const familyParents = parentsByFamily[family.id] || [];
        const familyStudents = studentsByFamily[family.id] || [];
        
        return (
          family.name?.toLowerCase().includes(query) ||
          familyParents.some(parent => 
            parent.first_name?.toLowerCase().includes(query) ||
            parent.last_name?.toLowerCase().includes(query) ||
            parent.email?.toLowerCase().includes(query)
          ) ||
          familyStudents.some(student => 
            student.first_name?.toLowerCase().includes(query) ||
            student.last_name?.toLowerCase().includes(query)
          )
        );
      });
    }

    // Apply status filter
    if (activeFilter !== "all") {
      filtered = filtered.filter(family => {
        const familyParents = parentsByFamily[family.id] || [];
        const familyStudents = studentsByFamily[family.id] || [];
        
        switch (activeFilter) {
          case "needs-parent":
            return familyParents.length === 0;
          case "needs-student":
            return familyStudents.length === 0;
          case "active":
            return familyParents.length > 0 && familyStudents.length > 0;
          case "inactive":
            return familyParents.length === 0 || familyStudents.length === 0;
          default:
            return true;
        }
      });
    }

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [families, parentsByFamily, studentsByFamily, debouncedSearchQuery, activeFilter]);

  // Early return if no organization ID
  if (!organizationId) {
    return (
      <AdminPageScaffold
        title="Families"
        description="Manage the families in your activity center community"
        icon={Users}
        error={new Error("Loading organization details...")}
      >
        <div />
      </AdminPageScaffold>
    );
  }
  
  const handleFamilySelect = useCallback((familyId: string) => {
    navigate(`/admin/families/${familyId}`);
  }, [navigate]);
  
  const handleAddFamily = useCallback(() => {
    setIsAddFamilyDialogOpen(true);
    setIsAddFamilyWithParentDialogOpen(false);
  }, [setIsAddFamilyDialogOpen, setIsAddFamilyWithParentDialogOpen]);
  
  const handleAddFamilyWithParent = useCallback(() => {
    setIsAddFamilyWithParentDialogOpen(true);
    setIsAddFamilyDialogOpen(false);
  }, [setIsAddFamilyWithParentDialogOpen, setIsAddFamilyDialogOpen]);

  const handleSendInvite = useCallback((parentId: string) => {
    sendInvite(parentId, parentsByFamily[selectedFamilyId || ''] || [], setAllParents);
  }, [sendInvite, parentsByFamily, selectedFamilyId, setAllParents]);

  // Family card renderer
  const renderFamilyCard = (family: Family, index: number) => (
    <FamilyCard
      key={family.id}
      family={family}
      parents={parentsByFamily[family.id] || []}
      students={studentsByFamily[family.id] || []}
      onClick={() => handleFamilySelect(family.id)}
    />
  );

  // Family table row renderer
  const renderFamilyTableRow = (family: Family, index: number) => (
    <FamilyTableRow
      key={family.id}
      family={family}
      parents={parentsByFamily[family.id] || []}
      students={studentsByFamily[family.id] || []}
      onClick={() => handleFamilySelect(family.id)}
      onSendInvite={handleSendInvite}
      onEditParent={handleEditParent}
    />
  );

  // Table header renderer
  const renderTableHeader = () => (
    <tr>
      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Family
      </th>
      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Parents
      </th>
      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Students
      </th>
      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Status
      </th>
      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Actions
      </th>
    </tr>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="text-center py-16">
      <div className="mx-auto w-24 h-24 bg-pink-50 rounded-full flex items-center justify-center mb-6">
        <Heart className="h-12 w-12 text-pink-500" />
      </div>
      <h3 className="text-xl font-semibold mb-2">
        {searchQuery || activeFilter !== "all" 
          ? "No families match your search" 
          : "No families yet"
        }
      </h3>
      <p className="text-muted-foreground mb-8 max-w-md mx-auto">
        {searchQuery || activeFilter !== "all"
          ? "Try adjusting your search terms or filters to find the families you're looking for."
          : "Start building your community by adding your first family. Each family can have multiple parents and students."
        }
      </p>
      <div className="flex gap-3 justify-center">
        <Button onClick={handleAddFamily} variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          Add Family
        </Button>
        <Button onClick={handleAddFamilyWithParent}>
          <UserPlus className="h-4 w-4 mr-2" />
          Add Family with Parent
        </Button>
      </div>
    </div>
  );

  // Page actions
  const pageActions = (
    <div className="flex gap-2">
      <Button onClick={handleAddFamily} variant="outline" size="sm">
        <Plus className="h-4 w-4 mr-2" />
        Add Family
      </Button>
      <Button onClick={handleAddFamilyWithParent} size="sm">
        <UserPlus className="h-4 w-4 mr-2" />
        Add Family with Parent
      </Button>
    </div>
  );

  // Enhanced dialog handlers
  const handleFamilyAddedAdapter = (family: Family, updatedFamilies: Family[]) => {
    setFamilies(updatedFamilies);
    handleFamilyAdded(family, setFamilies);
    toast.success(`Family "${family.name}" added successfully!`);
  };
  
  const handleFamilyWithParentAddedAdapter = (family: Family, parent: Parent, updatedFamilies: Family[]) => {
    setFamilies(updatedFamilies);
    setAllParents(prev => [...prev, parent]);
    toast.success(`Family "${family.name}" with parent added successfully!`);
  };

  // Filter bar component
  const renderFilterBar = () => {
    const filterOptions = [
      { key: "all", label: "All Families", icon: UsersIcon, count: families.length },
      { key: "active", label: "Complete", icon: UserCheck, count: families.filter(f => {
        const parents = parentsByFamily[f.id] || [];
        const students = studentsByFamily[f.id] || [];
        return parents.length > 0 && students.length > 0;
      }).length },
      { key: "needs-parent", label: "Needs Parent", icon: AlertCircle, count: families.filter(f => {
        const parents = parentsByFamily[f.id] || [];
        return parents.length === 0;
      }).length },
      { key: "needs-student", label: "Needs Student", icon: AlertCircle, count: families.filter(f => {
        const students = studentsByFamily[f.id] || [];
        return students.length === 0;
      }).length },
      { key: "inactive", label: "Incomplete", icon: AlertCircle, count: families.filter(f => {
        const parents = parentsByFamily[f.id] || [];
        const students = studentsByFamily[f.id] || [];
        return parents.length === 0 || students.length === 0;
      }).length }
    ];

    return (
      <div className="flex flex-wrap gap-2 mb-6">
        {filterOptions.map((option) => {
          const Icon = option.icon;
          const isActive = activeFilter === option.key;
          const needsAttention = option.key === "needs-parent" || option.key === "needs-student" || option.key === "inactive";
          
          return (
            <Button
              key={option.key}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveFilter(option.key as FilterType)}
              className={`flex items-center gap-2 transition-all duration-200 ${
                isActive ? "shadow-sm" : "hover:shadow-sm"
              } ${needsAttention && option.count > 0 && !isActive ? "border-orange-200 text-orange-700 hover:bg-orange-50" : ""}`}
            >
              <Icon className="h-4 w-4" />
              <span>{option.label}</span>
              <Badge 
                variant={isActive ? "secondary" : "outline"} 
                className={`ml-1 text-xs ${needsAttention && !isActive ? "bg-orange-100 text-orange-700 border-orange-200" : ""}`}
              >
                {option.count}
              </Badge>
            </Button>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <StandardPageHeader
        title="Families"
        subtitle="Manage the families in your hive"
        icon={Users}
        actions={pageActions}
      />
      
      <AdminPageScaffold
        loading={loading || subscriptionLoading}
        error={connectionError ? new Error("Connection error loading families") : null}
        isEmpty={filteredFamilies.length === 0 && !loading && !subscriptionLoading}
        emptyState={<EmptyState />}
      >
        {isHiveShareOnly ? (
          /* HiveShare Simplified UI */
          <div className="space-y-6">
            {/* Search */}
            <div className="mb-6">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                onClear={clearSearch}
                placeholder="Search families..."
                className="w-full max-w-md"
                showShortcutHint={false}
              />
            </div>

            {/* Filter Bar */}
            {renderFilterBar()}

            {/* Simple family list */}
            <DataViewScaffold
              items={filteredFamilies}
              loading={loading}
              views={['card']}
              defaultView="card"
              storageKey="hiveshare-families-view"
              cardColumns={{ sm: 1, md: 2, lg: 3 }}
              renderCard={renderFamilyCard}
              emptyState={<EmptyState />}
              onItemClick={(family) => handleFamilySelect(family.id)}
            />
          </div>
        ) : (
          /* Full Hivetivity UI */
          <>
            {/* Search */}
            <div className="mb-6">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                onClear={clearSearch}
                placeholder="Search families, parents, or children..."
                className="w-full max-w-md"
                showShortcutHint={false}
              />
            </div>

            {/* Filter Bar */}
            {renderFilterBar()}

            {/* Data View */}
            <DataViewScaffold
              items={filteredFamilies}
              loading={loading}
              views={['card', 'table']}
              defaultView="table"
              storageKey="families-view"
              cardColumns={{ sm: 1, md: 2, lg: 3, xl: 4 }}
              renderCard={renderFamilyCard}
              renderTableRow={renderFamilyTableRow}
              renderTableHeader={renderTableHeader}
              emptyState={<EmptyState />}
              onItemClick={handleFamilySelect}
            />
          </>
        )}
      </AdminPageScaffold>

      {/* Dialogs - available for both user types */}
      <AddFamilyDialog 
        open={isAddFamilyDialogOpen} 
        onClose={() => setIsAddFamilyDialogOpen(false)} 
        onFamilyAdded={handleFamilyAddedAdapter} 
      />

      <AddFamilyWithParentDialog 
        open={isAddFamilyWithParentDialogOpen} 
        onClose={() => setIsAddFamilyWithParentDialogOpen(false)} 
        onFamilyWithParentAdded={handleFamilyWithParentAddedAdapter} 
      />
    </>
  );
};

export default Families;